import { useCallback, useEffect, useState } from 'react'

import { useParams, useNavigate } from 'react-router-dom'

import { useAuth } from '@/core/hooks/useAuth'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import { Card, CardContent } from '@/shared/components/molecules/Card/Card'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

export const LoginAsUserPage = () => {
	const { token } = useParams<{ token: string }>()
	const navigate = useNavigate()

	const { t } = useTranslation()
	const { t: tAuth } = useTranslation('auth')
	const { loginWithToken, isLoading, isAuthenticated, error, isInitialized } =
		useAuth()

	const [isConfirmOpen, setIsConfirmOpen] = useState(false)
	const [isSwitchingUser, setIsSwitchingUser] = useState(false)

	const handleCancel = useCallback(() => {
		setIsSwitchingUser(false)
		setIsConfirmOpen(false)
		navigate('/')
	}, [navigate])

	const handleContinue = useCallback(() => {
		if (!token) return
		setIsSwitchingUser(true)
		setIsConfirmOpen(false)
		loginWithToken({ token }, '/')
	}, [loginWithToken, token])

	useEffect(() => {
		if (!isInitialized) return

		if (!token) {
			navigate('/login')
			return
		}

		if (isAuthenticated && !isSwitchingUser) {
			setIsConfirmOpen(true)
			return
		}

		if (!isAuthenticated) {
			loginWithToken({ token }, '/')
		}
	}, [
		isAuthenticated,
		isInitialized,
		isSwitchingUser,
		loginWithToken,
		navigate,
		token,
	])

	useEffect(() => {
		if (!error) return
		// Redirect, since the redux state will show the error in the login page
		navigate('/login')
	}, [error, navigate])

	if (isLoading || !isInitialized || isSwitchingUser) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Card className="w-full max-w-md">
					<CardContent className="p-6">
						<div className="space-y-4">
							<Skeleton className="h-8 w-full" />
							<Skeleton className="h-4 w-3/4" />
							<Skeleton className="h-4 w-1/2" />
						</div>
					</CardContent>
				</Card>
			</div>
		)
	}

	return (
		<>
			<Dialog
				open={isConfirmOpen}
				onOpenChange={(open) => !open && handleCancel()}
			>
				<DialogContent
					className="max-w-md p-0 gap-0 bg-background"
					showCloseButton={false}
					onInteractOutside={(e) => e.preventDefault()}
				>
					<DialogHeader className="px-6 pt-5 pb-3">
						<DialogTitle className="text-xl">
							{tAuth('loginAsUser.confirm.title')}
						</DialogTitle>
						<DialogDescription>
							{tAuth('loginAsUser.confirm.description')}
						</DialogDescription>
					</DialogHeader>

					<DialogFooter className="px-6 py-4">
						<Button
							type="button"
							variant="outline"
							onClick={handleCancel}
							className="h-9 rounded-full px-6"
						>
							{t('actions.cancel')}
						</Button>
						<Button
							type="button"
							onClick={handleContinue}
							className="h-9 rounded-full px-6"
						>
							{t('actions.continue')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
